import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Badge as BadgeIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import Typography from "@/components/ui/typography";
import { generateFounderCertificate } from "@/features/founders/api/founders";
import { useToast } from "@/hooks/use-toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { FounderLevel } from "@/types/levels";

const CertificateSection = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  // Determine current level from user data
  const currentLevel: FounderLevel | null = (() => {
    const backendLevel = user?.level?.toLowerCase();
    if (!backendLevel) return null;
    if (backendLevel === "certified") return FounderLevel.STACK_CERTIFIED;
    if (backendLevel === "pro") return FounderLevel.STACK_PRO;
    if (backendLevel === "x") return FounderLevel.STACK_X;
    return null; // "starter" means no level unlocked
  })();

  const { mutate: generateCertificate, isPending: isGeneratingCertificate } =
    useMutation({
      mutationFn: generateFounderCertificate,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["me"] });
        toast({
          title: "Certificate generated!",
          description: "Your personalized certificate is ready.",
        });
      },
      onError: () => {
        toast({
          title: "Failed to generate certificate",
          description: "Please try again later.",
          variant: "destructive",
        });
      },
    });

  return (
    <div className="bg-[#f9f9f9] rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Typography size="l2" className="font-semibold text-[#494949]">
          Certificate
        </Typography>
        <span className="h-6 inline-flex items-center rounded px-[6px] py-[4px] text-xs font-semibold bg-[#dcddfa] text-[#4f55e8]">
          {currentLevel || FounderLevel.STACK_CERTIFIED}
          <BadgeIcon className="ml-1 h-4 w-4" />
        </span>
      </div>
      <Typography size="b2" className="text-[#767677]">
        Your personalized Stack certificate, showcasing your certified status.
      </Typography>

      {user?.certificateUrl && user.certificateUrl.length > 0 ? (
        <a
          href={user.certificateUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-lg border border-[#e8e8e8] overflow-hidden bg-white hover:border-[#d4d4d4] transition-colors cursor-pointer max-w-md"
        >
          <img
            src={user.certificateUrl}
            alt="Stack Certificate"
            className="w-full h-auto"
          />
        </a>
      ) : (
        <div className="space-y-3">
          <Typography size="b2" className="text-[#767677]">
            Generate your personalized certificate to share your achievement
            with the world.
          </Typography>
          <Button
            onClick={() => generateCertificate()}
            disabled={isGeneratingCertificate}
            className="text-white rounded-lg h-9 px-5"
          >
            {isGeneratingCertificate && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Generate your personal certificate
          </Button>
        </div>
      )}
    </div>
  );
};

export default CertificateSection;
